import { useCallback, useEffect, useRef, useState } from 'react';

import { usePaymentErrorContext } from '../contexts/payment-error-context';
import { PaymentProcessor } from '../core/payment-processor';
import { AvailablePaymentMethodsV1Dto } from '../types';

interface UseAvailablePaymentMethodsState {
    methods: AvailablePaymentMethodsV1Dto[];
    isLoading: boolean;
    hasLoaded: boolean;
    hasError: boolean;
}

interface UseAvailablePaymentMethodsResult
    extends UseAvailablePaymentMethodsState {
    initializeIfNeeded: () => Promise<void>;
    refetch: () => Promise<void>;
}

export const useAvailablePaymentMethods = (
    paymentProcessor: PaymentProcessor
): UseAvailablePaymentMethodsResult => {
    const [state, setState] = useState<UseAvailablePaymentMethodsState>({
        methods: [],
        isLoading: true,
        hasLoaded: false,
        hasError: false
    });

    const { addError } = usePaymentErrorContext();
    const isMountedRef = useRef(false);
    const hasFetchedRef = useRef(false);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const fetchAvailablePaymentMethods = useCallback(
        async (isRefetch = false) => {
            if (!isRefetch && (state.hasLoaded || hasFetchedRef.current)) {
                return;
            }

            hasFetchedRef.current = true;

            if (isMountedRef.current) {
                setState((prev) => ({ ...prev, isLoading: true }));
            }

            try {
                const methods =
                    await paymentProcessor.getAvailablePaymentMethods();

                if (isMountedRef.current) {
                    setState({
                        methods,
                        isLoading: false,
                        hasLoaded: true,
                        hasError: false
                    });
                }
            } catch (error) {
                if (isMountedRef.current) {
                    setState({
                        methods: [],
                        isLoading: false,
                        hasLoaded: true,
                        hasError: true
                    });
                    addError(error);
                }
            }
        },
        [paymentProcessor, addError, state.hasLoaded]
    );

    useEffect(() => {
        void fetchAvailablePaymentMethods();
    }, [fetchAvailablePaymentMethods]);

    const initializeIfNeeded = useCallback(async () => {
        if (!state.hasLoaded && !state.isLoading) {
            await fetchAvailablePaymentMethods();
        }
    }, [fetchAvailablePaymentMethods, state.hasLoaded, state.isLoading]);

    const refetch = useCallback(async () => {
        hasFetchedRef.current = false;
        setState({
            methods: [],
            isLoading: true,
            hasLoaded: false,
            hasError: false
        });
        await fetchAvailablePaymentMethods(true);
    }, [fetchAvailablePaymentMethods]);

    return {
        ...state,
        initializeIfNeeded,
        refetch
    };
};
